import { useState } from 'react'

import type { GridApi } from 'ag-grid-enterprise'

import { AgGridWidget, createContextMenu } from '@fortem-monorepo/react-ag'
import { HeaderConfigBar } from '@fortem-monorepo/react-spa'
import { Button } from '@fortem-monorepo/react-ui'
import { getErrorMessage } from '@fortem-monorepo/web-sdk'

import { CanAccess } from '@/components/CanAccess'
import { PERMISSIONS } from '@/constants/permissions'
import { useUserContext } from '@/index'
import { isFutureBalanceDate } from '@/lib/accrued-interest-dates'
import { useAccruedInterestBalances } from '@/react-query/queries/accrued-interests'

import { useAccruedInterestActions } from '../AccruedInterestActions.context'
import { useAccruedInterestsContext } from '../AccruedInterests.context'
import { type BalanceDisplayRow, freezeDisplayedBalances, withBalanceDrafts } from '../balance-drafts'
import { aggregateBalanceRows } from '../balance-group'
import { getBalanceStatus } from '../balance-status'
import { balanceColumns, centeredColumn } from './columns'

export const AccruedBalancesTab = () => {
  const { params, balanceDrafts, clearDrafts } = useAccruedInterestsContext()
  const query = useAccruedInterestBalances(params)
  const actions = useAccruedInterestActions()
  const [grid, setGrid] = useState<GridApi<BalanceDisplayRow> | null>(null)
  const { hasPermissions } = useUserContext()
  const canWrite = hasPermissions((permissions) => ({ permissions: [permissions.WRITE_OPERATIONS] }))
  const draftCount = Object.keys(balanceDrafts).length
  const futureDate = isFutureBalanceDate(params.value_date)
  const rows = futureDate ? [] : withBalanceDrafts(query.data ?? [], balanceDrafts)

  const afterDiscard = (description: string, next: () => void) => {
    if (actions.actionState) return

    if (!draftCount) {
      next()
      return
    }

    actions.openDiscardDrafts({
      description,
      onDiscard: () => {
        clearDrafts(params)
        next()
      },
    })
  }

  const guardedQuery = {
    ...query,
    refetch: async () => {
      if (!params.fund_name || futureDate || actions.actionState) return query
      if (draftCount) {
        afterDiscard(`Refresh balances for ${params.fund_name} on ${params.value_date}.`, () => {
          void query.refetch()
        })
        return query
      }
      return query.refetch()
    },
  }

  const menu = createContextMenu<BalanceDisplayRow>((event) => {
    const row = event.node?.data
    const defaults = event.defaultItems ?? []
    if (!row || event.node?.group || !canWrite || futureDate) return defaults

    return [
      {
        name: 'Correct opening and closing balances',
        disabled: query.isFetching || !!actions.actionState,
        action: () =>
          actions.openBalanceCorrection({
            params: { ...params },
            row: {
              ...row,
              revision: query.data?.find((source) => source.trade_id === row.trade_id)?.revision ?? row.revision,
            },
          }),
      },
      'separator',
      ...defaults,
    ]
  })

  const confirmDisplayed = () => {
    if (!grid || !canWrite || futureDate || query.isFetching || actions.actionState) return

    const selection = freezeDisplayedBalances(grid)
    if (selection.length) actions.openConfirmBalances({ params: { ...params }, rows: selection })
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      <HeaderConfigBar className="shrink-0 flex-wrap items-end gap-4">
        <CanAccess permissions={[PERMISSIONS.WRITE_OPERATIONS]}>
          <Button
            type="button"
            size="sm"
            disabled={!grid || !rows.length || futureDate || query.isFetching || !!actions.actionState}
            onClick={confirmDisplayed}
          >
            Confirm displayed balances
          </Button>
        </CanAccess>
        {draftCount > 0 && (
          <>
            <span role="status" className="text-sm text-amber-700">
              {draftCount} pending corrections
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={!!actions.actionState || query.isFetching}
              onClick={() =>
                afterDiscard(
                  `Discard pending corrections for ${params.fund_name} on ${params.value_date}.`,
                  () => undefined,
                )
              }
            >
              Discard corrections
            </Button>
          </>
        )}
      </HeaderConfigBar>
      {!params.fund_name && (
        <p className="shrink-0 p-3 text-sm text-muted-foreground">
          Select a fund and date to preview accrued interest balances.
        </p>
      )}
      {futureDate && (
        <p role="status" className="shrink-0 p-3 text-sm text-muted-foreground">
          Balances can only be computed for today or earlier (London time). Choose another date.
        </p>
      )}
      {query.isError && (
        <p role="alert" className="shrink-0 p-3 text-sm text-destructive">
          Unable to load accrued interest balances. {getErrorMessage(query.error)}
        </p>
      )}
      <AgGridWidget
        query={guardedQuery}
        widget={{ exposed: true, className: 'min-h-0 flex-1' }}
        widgetHeader={{ title: 'Accrued interest balances', className: 'shrink-0' }}
        widgetContent={{ className: 'min-h-0 flex-1 overflow-hidden' }}
        grid={{
          rowData: rows,
          columnDefs: balanceColumns,
          defaultColDef: centeredColumn,
          autoGroupColumnDef: {
            headerName: 'Instrument',
            minWidth: 260,
            pinned: 'left',
            cellStyle: { textAlign: 'center' },
            headerClass: 'header-center',
          },
          groupDefaultExpanded: 0,
          suppressAggFuncInHeader: true,
          getGroupRowAgg: ({ nodes }) =>
            aggregateBalanceRows(
              nodes.flatMap((node) => {
                const row = node.group
                  ? node.aggData
                  : node.data && { ...node.data, balance_status: getBalanceStatus(node.data) }
                return row ? [row] : []
              }),
            ),
          domLayout: 'normal',
          getContextMenuItems: menu,
          onGridReady: (event) => setGrid(event.api),
          getRowId: ({ data }: { data: BalanceDisplayRow }) => data.trade_id,
        }}
      />
    </div>
  )
}
